import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useClinic } from "@/context/ClinicContext";
import EmptyState from "@/components/EmptyState";
import { CardSkeleton, Skeleton } from "@/components/Skeleton";
import { useLocations } from "@/lib/useLocations";
import { isMultiLocation, locationLabel } from "@/lib/locations";
import { CalendarDays, CalendarPlus, Check, ChevronLeft, Loader2, MapPin, Video } from "lucide-react";

function fmt(startUtc, tz) {
  return new Date(startUtc).toLocaleString(undefined, {
    weekday: "short", month: "short", day: "numeric",
    hour: "numeric", minute: "2-digit", timeZone: tz,
  });
}

function dayLabel(startUtc, tz) {
  return new Date(startUtc).toLocaleDateString(undefined, {
    weekday: "long", month: "short", day: "numeric", timeZone: tz,
  });
}

function timeLabel(startUtc, tz) {
  return new Date(startUtc).toLocaleTimeString(undefined, {
    hour: "numeric", minute: "2-digit", timeZone: tz,
  });
}

function hoursUntil(startUtc) {
  return (new Date(startUtc) - new Date()) / 3600000;
}

/** How a past appointment should read. A visit that was MOVED is not a cancellation —
 *  the row exists only because the record keeps history, and calling it "cancelled"
 *  tells the patient something untrue about their own care. */
function pastLabel(a, tz) {
  if (a.status === "cancelled" && a.cancellation_kind === "rescheduled") {
    return {
      text: a.rescheduled_to ? "Moved" : "Moved to a new time",
      tone: "text-muted-foreground",
      strike: false,
    };
  }
  if (a.status === "cancelled") return { text: "Cancelled", tone: "text-muted-foreground", strike: true };
  if (a.status === "no_show") return { text: "Missed", tone: "text-amber-700", strike: false };
  if (a.status === "completed") return { text: "Completed", tone: "text-emerald-700", strike: false };
  return { text: a.status, tone: "text-muted-foreground", strike: false };
}

export default function PatientAppointments() {
  const { clinic } = useClinic();
  const tz = clinic?.timezone || "America/New_York";
  const cancelCutoff = clinic?.cancel_cutoff_hours ?? 24;
  // Where to go only matters when there is more than one answer.
  const { locations } = useLocations({ portal: "patient" });
  const showLocation = isMultiLocation(locations || []);
  const rescheduleCutoff = clinic?.reschedule_cutoff_hours ?? 24;

  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [moving, setMoving] = useState(null);      // appointment being rescheduled
  const [slots, setSlots] = useState(null);

  const load = useCallback(() => {
    api.get("/patient/appointments")
      .then((r) => setData(r.data))
      .catch((e) => { setData({ upcoming: [], past: [] }); setError(formatApiError(e.response?.data?.detail)); });
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!moving) { setSlots(null); return; }
    api.get("/availability", { params: { visit_type_id: moving.visit_type_id, provider_id: moving.provider_id } })
      .then((r) => setSlots(r.data.slots || []))
      .catch(() => setSlots([]));
  }, [moving]);

  const byDay = useMemo(() => {
    const groups = new Map();
    (slots || []).forEach((s) => {
      const k = new Date(s.start_utc).toLocaleDateString("en-CA", { timeZone: tz });
      if (!groups.has(k)) groups.set(k, []);
      groups.get(k).push(s);
    });
    return [...groups.entries()];
  }, [slots, tz]);

  async function cancel(a) {
    if (!window.confirm(`Cancel your ${a.type} on ${fmt(a.start_utc, tz)}?`)) return;
    setBusy(a.id); setError("");
    try {
      await api.post(`/patient/appointments/${a.id}/cancel`, {});
      load();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    } finally { setBusy(""); }
  }

  async function reschedule(slot) {
    setBusy(moving.id); setError("");
    try {
      await api.post(`/patient/appointments/${moving.id}/reschedule`, { start_utc: slot.start_utc });
      setMoving(null);
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      // The backend hands back the nearest alternatives when it loses the slot, so the
      // patient can pick again in place rather than starting over.
      if (detail && typeof detail === "object" && detail.alternatives) {
        setError(detail.message);
        setSlots(detail.alternatives);
      } else {
        setError(formatApiError(detail));
      }
    } finally { setBusy(""); }
  }

  if (!data) return <div className="space-y-4"><Skeleton className="h-8 w-56" /><CardSkeleton lines={3} /></div>;

  // --- reschedule picker -----------------------------------------------------
  if (moving) {
    return (
      <div className="space-y-5" data-testid="reschedule-picker">
        <button onClick={() => { setMoving(null); setError(""); }} data-testid="reschedule-back"
                className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ChevronLeft className="w-4 h-4" /> Back to my appointments
        </button>
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">Move your visit</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {moving.type} — currently {fmt(moving.start_utc, tz)}. Your existing time stays
            booked until you pick a new one.
          </p>
        </div>

        {error && (
          <p className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-lg px-3 py-2"
             data-testid="reschedule-error">{error}</p>
        )}

        {slots === null ? <Skeleton className="h-24 w-full" />
          : slots.length === 0 ? (
            <EmptyState icon={CalendarDays} title="No other open times right now"
                        description="Message the clinic and we'll find something."
                        testid="no-reschedule-slots" />
          ) : (
            <div className="space-y-5" data-testid="reschedule-slot-list">
              {byDay.map(([day, daySlots]) => (
                <div key={day}>
                  <p className="text-sm font-medium mb-2">{dayLabel(daySlots[0].start_utc, tz)}</p>
                  <div className="flex flex-wrap gap-2">
                    {daySlots.map((s) => (
                      <button key={s.start_utc} data-testid={`reschedule-slot-${s.start_utc}`}
                              disabled={!!busy} onClick={() => reschedule(s)}
                              className="px-3 py-2 rounded-md border border-border bg-card text-sm hover:border-primary hover:text-primary transition-all disabled:opacity-50">
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : timeLabel(s.start_utc, tz)}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
      </div>
    );
  }

  // --- list ------------------------------------------------------------------
  return (
    <div className="space-y-6" data-testid="patient-appointments">
      <div className="flex items-center justify-between gap-3">
        <h1 className="font-heading text-2xl font-bold tracking-tight">My appointments</h1>
        <Link to="/portal/book" data-testid="book-cta"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium">
          <CalendarPlus className="w-4 h-4" /> Book a visit
        </Link>
      </div>

      {error && (
        <p className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-lg px-3 py-2"
           data-testid="appointments-error">{error}</p>
      )}

      <section className="space-y-3">
        <h2 className="text-sm font-medium text-muted-foreground">Upcoming</h2>
        {data.upcoming.length === 0 ? (
          <EmptyState icon={CalendarDays} title="Nothing booked yet"
                      description="Pick a time that works for you — it takes about a minute."
                      testid="no-upcoming"
                      action={<Link to="/portal/book" className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium">Book a visit</Link>} />
        ) : data.upcoming.map((a) => {
          const hrs = hoursUntil(a.start_utc);
          const canCancel = hrs >= cancelCutoff;
          const canMove = hrs >= rescheduleCutoff && a.status === "scheduled";
          const inCutoff = !canCancel || !canMove;
          return (
            <div key={a.id} data-testid={`appt-${a.id}`}
                 className="bg-card border border-border rounded-xl p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-medium">{a.type}</p>
                  <p className="text-sm text-muted-foreground mt-0.5">{fmt(a.start_utc, tz)}</p>
                  {a.provider && <p className="text-xs text-muted-foreground mt-0.5">with {a.provider}</p>}
                  {showLocation && (() => {
                    const loc = (locations || []).find((l) => l.id === a.location_id);
                    return loc ? (
                      <p className="text-xs text-muted-foreground mt-0.5 inline-flex items-center gap-1"
                         data-testid={`appt-location-${a.id}`}>
                        <MapPin className="w-3 h-3" /> {locationLabel(loc)}
                      </p>
                    ) : null;
                  })()}
                  {a.status === "requested" && (
                    <p className="text-xs text-amber-700 mt-1.5">
                      Awaiting confirmation — the time isn't held yet.
                    </p>
                  )}
                  {a.telehealth?.join_url && (
                    <a href={a.telehealth.join_url} target="_blank" rel="noreferrer"
                       data-testid={`appt-join-${a.id}`}
                       className="inline-flex items-center gap-1 text-xs text-primary mt-1.5">
                      <Video className="w-3 h-3" /> Join link
                    </a>
                  )}
                </div>
                {/* Hidden, not disabled, inside the cutoff — a greyed button invites a
                    click that can only ever be refused. */}
                <div className="flex items-center gap-2">
                  {canMove && (
                    <button data-testid={`appt-reschedule-${a.id}`} onClick={() => setMoving(a)}
                            className="px-3 py-1.5 rounded-md border border-border text-sm font-medium hover:border-primary hover:text-primary transition-all">
                      Reschedule
                    </button>
                  )}
                  {canCancel && (
                    <button data-testid={`appt-cancel-${a.id}`} disabled={busy === a.id}
                            onClick={() => cancel(a)}
                            className="px-3 py-1.5 rounded-md border border-border text-sm font-medium text-muted-foreground hover:border-destructive hover:text-destructive transition-all disabled:opacity-50">
                      {busy === a.id ? <Loader2 className="w-4 h-4 animate-spin" /> : "Cancel"}
                    </button>
                  )}
                </div>
              </div>
              {inCutoff && (
                <p className="text-xs text-muted-foreground mt-3 border-t border-border pt-3"
                   data-testid="cutoff-message">
                  Changes aren't available within {Math.max(cancelCutoff, rescheduleCutoff)} hours
                  of your visit — <Link to="/portal/messages" className="text-primary">message the clinic</Link> and we'll help.
                </p>
              )}
            </div>
          );
        })}
      </section>

      <section className="space-y-3">
        <h2 className="text-sm font-medium text-muted-foreground">Past</h2>
        {data.past.length === 0 ? (
          <p className="text-sm text-muted-foreground">No past visits yet.</p>
        ) : (
          <div className="space-y-2" data-testid="past-appointments">
            {data.past.map((a) => {
              const l = pastLabel(a, tz);
              return (
                <div key={a.id} data-testid={`past-appt-${a.id}`}
                     className="bg-card border border-border rounded-lg px-4 py-3 flex items-center justify-between gap-3">
                  <div className={l.strike ? "line-through text-muted-foreground" : ""}>
                    <span className="text-sm font-medium">{a.type}</span>
                    <span className="text-sm text-muted-foreground"> · {fmt(a.start_utc, tz)}</span>
                  </div>
                  <span className={`text-xs ${l.tone}`} data-testid={`past-status-${a.id}`}>
                    {l.text === "Completed" && <Check className="w-3 h-3 inline mr-0.5" />}
                    {l.text}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
